import React, { useState } from 'react';
import { X, Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';
import { safeFetch } from '../../lib/api';
import { workspaceHeader } from '../../lib/workspaces';

interface AdminOrgInviteModalProps {
  isOpen: boolean;
  role: 'employer' | 'referrer' | 'sponsor';
  onClose: () => void;
  onSuccess: (result: any) => void;
  workspaceId: string;
}

interface FormData {
  email: string;
  firstName: string;
  lastName: string;
  companyName: string;
  companyAddress?: string;
  contactPhone?: string;
  website?: string;
  referrerType?: 'individual' | 'agency';
}

export const AdminOrgInviteModal: React.FC<AdminOrgInviteModalProps> = ({
  isOpen,
  role,
  onClose,
  onSuccess,
  workspaceId,
}) => {
  const [formData, setFormData] = useState<FormData>({
    email: '',
    firstName: '',
    lastName: '',
    companyName: '',
    companyAddress: '',
    contactPhone: '',
    website: '',
    referrerType: 'individual',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState(false);
  const [invitationId, setInvitationId] = useState<string>('');

  const handleChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (error) setError('');
  };

  const getRoleLabel = (): string => {
    if (role === 'employer') return 'Employer';
    if (role === 'referrer') return 'Referrer';
    return 'Sponsor';
  };

  const validateForm = (): string | null => {
    if (!formData.email.trim()) return 'Email is required';
    if (!formData.email.includes('@')) return 'Please enter a valid email';
    if (!formData.firstName.trim()) return 'First name is required';
    if (!formData.lastName.trim()) return 'Last name is required';
    if (!formData.companyName.trim()) return 'Company/Organization name is required';
    if (role === 'referrer' && !formData.referrerType) return 'Referrer type is required';
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const payload: Record<string, any> = {
        email: formData.email.toLowerCase().trim(),
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        companyName: formData.companyName.trim(),
      };

      if (formData.companyAddress) {
        payload.companyAddress = formData.companyAddress.trim();
      }
      if (formData.contactPhone) {
        payload.contactPhone = formData.contactPhone.trim();
      }
      if (formData.website) {
        payload.website = formData.website.trim();
      }

      if (role === 'referrer' && formData.referrerType) {
        payload.referrerType = formData.referrerType;
      }

      const response = await safeFetch(`/api/admin/${role}s/invite`, {
        method: 'POST',
        body: JSON.stringify(payload),
        headers: {
          'Content-Type': 'application/json',
          ...workspaceHeader(),
        },
      });

      setSuccess(true);
      setInvitationId(response.invitationId || '');

      // Reset form after success
      setTimeout(() => {
        setFormData({
          email: '',
          firstName: '',
          lastName: '',
          companyName: '',
          companyAddress: '',
          contactPhone: '',
          website: '',
          referrerType: 'individual',
        });
        onSuccess(response);
      }, 2000);
    } catch (err: any) {
      const errorMessage = err.message || `Failed to send invitation to ${getRoleLabel()}`;
      setError(errorMessage);
      console.error('Invitation error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="relative w-full max-w-md rounded-lg bg-white p-6 shadow-lg">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
          aria-label="Close modal"
        >
          <X size={20} />
        </button>

        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          Invite {getRoleLabel()}
        </h2>

        {success && (
          <div className="mb-4 p-4 bg-emerald-50 border border-emerald-200 rounded-lg flex items-start gap-3">
            <CheckCircle2 className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-emerald-900">
                Invitation sent successfully!
              </p>
              <p className="text-xs text-emerald-700 mt-1">
                Closing in 2 seconds...
              </p>
            </div>
          </div>
        )}

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
            <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {!success && (
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email Address <span className="text-red-500" aria-hidden="true">*</span><span className="sr-only"> (required)</span>
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
                disabled={loading}
                placeholder="user@example.com"
              />
            </div>

            {/* First Name */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  First Name <span className="text-red-500" aria-hidden="true">*</span><span className="sr-only"> (required)</span>
                </label>
                <input
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => handleChange('firstName', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
                  disabled={loading}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Last Name <span className="text-red-500" aria-hidden="true">*</span><span className="sr-only"> (required)</span>
                </label>
                <input
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => handleChange('lastName', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
                  disabled={loading}
                />
              </div>
            </div>

            {/* Company Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {role === 'employer' ? 'Company Name' : role === 'sponsor' ? 'Organization Name' : 'Agency/Company Name'}{' '}
                <span className="text-red-500" aria-hidden="true">*</span><span className="sr-only"> (required)</span>
              </label>
              <input
                type="text"
                value={formData.companyName}
                onChange={(e) => handleChange('companyName', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
                disabled={loading}
                placeholder={role === 'employer' ? 'Company Name' : 'Agency or Organization'}
              />
            </div>

            {/* Referrer Type (for referrers only) */}
            {role === 'referrer' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Referrer Type <span className="text-red-500" aria-hidden="true">*</span><span className="sr-only"> (required)</span>
                </label>
                <div className="flex gap-3">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="referrerType"
                      value="individual"
                      checked={formData.referrerType === 'individual'}
                      onChange={(e) => handleChange('referrerType', e.target.value)}
                      disabled={loading}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700">Individual</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="referrerType"
                      value="agency"
                      checked={formData.referrerType === 'agency'}
                      onChange={(e) => handleChange('referrerType', e.target.value)}
                      disabled={loading}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700">Agency/Partner</span>
                  </label>
                </div>
              </div>
            )}

            {/* Company Address (optional) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Company Address <span className="text-gray-500">(optional)</span>
              </label>
              <input
                type="text"
                value={formData.companyAddress}
                onChange={(e) => handleChange('companyAddress', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
                disabled={loading}
              />
            </div>

            {/* Contact Phone (optional) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Contact Phone <span className="text-gray-500">(optional)</span>
              </label>
              <input
                type="tel"
                value={formData.contactPhone}
                onChange={(e) => handleChange('contactPhone', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
                disabled={loading}
              />
            </div>

            {/* Website (optional) */}
            {role === 'employer' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Website <span className="text-gray-500">(optional)</span>
                </label>
                <input
                  type="url"
                  value={formData.website}
                  onChange={(e) => handleChange('website', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
                  disabled={loading}
                  placeholder="https://example.com"
                />
              </div>
            )}

            {/* Buttons */}
            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className="flex-1 px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading && <Loader2 size={16} className="animate-spin" />}
                Send Invitation
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};
